import {
  faArrowRotateLeft,
  faCalendarDays,
  faCameraRetro,
  faClockRotateLeft,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FlexBox, JoonUIColor, SPACING, Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { QuestDetailsCard } from "./QuestDetailsCard"
import { useViewQuestModalStore } from "./useViewQuestModalStore"
import { SplotchSolidExclamationIcon } from "../../../components/icons/SplotchSolidExclamationIcon"
import {
  getRepetitionArrayFromRrule,
  getRepetitionText,
  getRepetitionIntervalFromRrule,
  isCoachQuest,
} from "../../../util/util"
import CareTeamAvatar from "../../care/CareTeamAvatar"
import useCareTeam from "../../care/useCareTeam"
import {
  ROUTINE_LABEL_KEYS,
  SKILL_LABEL_KEYS,
} from "../addEditQuestModal/questLabelKeys"

export const QuestDetails = () => {
  const { questInstance } = useViewQuestModalStore()
  const { careTeam } = useCareTeam()
  const { t } = useTranslation()

  const questSeriesData = questInstance?.series

  const mandatory = questSeriesData?.mandatory
  const skill = questSeriesData?.skill
  const byWeekday = getRepetitionArrayFromRrule(
    questSeriesData?.recurrence || ""
  )
  const hasRepetition = !byWeekday.every((day) => !day)
  const interval = getRepetitionIntervalFromRrule(
    questSeriesData?.recurrence || ""
  )
  const startDate = questSeriesData?.start_date
  const endDate = questSeriesData?.end_date
  const recurrence = getRepetitionText(byWeekday, interval)
  const routine = questSeriesData?.routine
  const timerLength = questSeriesData?.timer_length
  const reqReview = questSeriesData?.requires_review
  const reqPhoto = questSeriesData?.requires_completion_photo
  const reqNotes = questSeriesData?.requires_completion_notes
  const requiresReviewText = reqReview
    ? reqPhoto && reqNotes
      ? t("QD_REVIEW_PHOTO_NOTES")
      : reqPhoto
        ? t("QD_REVIEW_PHOTO")
        : reqNotes
          ? t("QD_REVIEW_NOTES")
          : t("QD_REVIEW")
    : t("QD_NO_COMPLETION_REQ")
  const routineLabel = routine ? t(ROUTINE_LABEL_KEYS[routine]) : ""
  const relativeDate = dayjs(startDate || undefined).isSame(dayjs(), "day")
    ? t("QUEST_TODAY")
    : dayjs(startDate).format("MMM D, YYYY")
  const dateTimeText = hasRepetition
    ? dayjs(startDate || undefined).isBefore(dayjs(), "day")
      ? t("QD_DT_STARTED", { routine: routineLabel, date: relativeDate })
      : t("QD_DT_STARTS", { routine: routineLabel, date: relativeDate })
    : endDate
      ? t("QD_DT_DUE", { routine: routineLabel, date: relativeDate })
      : t("QD_DT_ONGOING", { routine: routineLabel })

  const labels = useMemo(() => {
    if (!questSeriesData) return []
    const labels = [
      // Mandatory
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        <SplotchSolidExclamationIcon
          color={
            mandatory ? JoonUIColor.icon.accent : JoonUIColor.text.disabled
          }
          size={15}
        />
        <Typography variant="bodySmall">
          {mandatory ? t("QUEST_MANDATORY") : t("QD_OPTIONAL_QUEST")}
        </Typography>
      </FlexBox>,

      // Skill
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        {skill ? (
          <>
            <img
              src={`/images/icons/${skill}-icon.png`}
              alt={skill || ""}
              style={{ width: "15px" }}
            />
            <Typography variant="bodySmall">
              {t(SKILL_LABEL_KEYS[skill])}
            </Typography>
          </>
        ) : (
          <>
            <FontAwesomeIcon
              icon={faXmark}
              color={JoonUIColor.icon.neutral}
              style={{ width: "15px" }}
            />
            <Typography variant="bodySmall">{t("QD_NO_SKILL_SET")}</Typography>
          </>
        )}
      </FlexBox>,

      // Recurrence
      ...(hasRepetition
        ? [
            <FlexBox
              direction="row"
              align="center"
              gap={SPACING.space2}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faArrowRotateLeft}
                color={JoonUIColor.icon.accent}
                style={{ width: "15px" }}
              />
              <Typography variant="bodySmall">{recurrence}</Typography>
            </FlexBox>,
          ]
        : []),

      // Start date
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        <FontAwesomeIcon
          icon={faCalendarDays}
          color={JoonUIColor.icon.accent}
          style={{ width: "15px" }}
        />
        <Typography variant="bodySmall">{dateTimeText}</Typography>
      </FlexBox>,

      // Timer length
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        <FontAwesomeIcon
          icon={faClockRotateLeft}
          color={
            timerLength ? JoonUIColor.icon.accent : JoonUIColor.icon.neutral
          }
          style={{ width: "15px" }}
        />
        <Typography variant="bodySmall">
          {timerLength
            ? t("QD_TIMER", {
                minutes: timerLength.slice(3, 5),
                seconds: timerLength.slice(6, 8),
              })
            : t("QD_NO_TIMER")}
        </Typography>
      </FlexBox>,

      // Requires review
      <FlexBox direction="row" align="center" gap={SPACING.space2} wrap={false}>
        <FontAwesomeIcon
          icon={faCameraRetro}
          color={reqReview ? JoonUIColor.icon.accent : JoonUIColor.icon.neutral}
          style={{ width: "15px" }}
        />
        <Typography variant="bodySmall">{requiresReviewText}</Typography>
      </FlexBox>,
    ]

    if (isCoachQuest(questInstance)) {
      const assignedCoach = careTeam?.find(
        (profile) =>
          profile.user.id === questInstance?.series?.assigner_profile?.user_id
      )
      if (assignedCoach)
        labels.unshift(
          <FlexBox
            direction="row"
            align="center"
            gap={SPACING.space2}
            wrap={false}
          >
            {assignedCoach?.profile_image_url && (
              <CareTeamAvatar profile={assignedCoach} size="xxs" />
            )}
            <Typography variant="bodySmall">
              {t("QD_ASSIGNED_BY_COACH", {
                name: assignedCoach?.nickname ?? assignedCoach?.user.name ?? "",
              })}
            </Typography>
          </FlexBox>
        )
    }
    return labels
  }, [
    questInstance,
    questSeriesData,
    mandatory,
    skill,
    hasRepetition,
    recurrence,
    timerLength,
    reqReview,
    dateTimeText,
    requiresReviewText,
    careTeam,
    t,
  ])

  if (!questInstance) return <></>

  return (
    <div className="flex-col rounded-md overflow-hidden border border-borderPrimary w-full flex">
      {labels.map((label, index) => (
        <QuestDetailsCard
          key={index}
          label={label}
          borderBottom={index !== labels.length - 1}
        />
      ))}
    </div>
  )
}
